import * as React from 'react';

import { useApolloClientHeaders } from '@hooks/useApolloClientHeaders';

import { SkinAnalyzerConfig } from '../../model';
import { SkaConfig } from '../model';
import { initSkinAnalyzer, unmountSkinAnalyzer } from '../skinAnalyzerScripts';
import { trackViewOrSelectPromotion } from '../tracking';
import { skaCountryConfig } from '../utils';

import { useSkinAnalyzerEvents } from './useSkinAnalyzerEvents';
import { useTracking } from './useTracking';
import { SkaTrackingEvent } from './useTracking/model';

interface UseInitSkinAnalyzerArgs {
  inView: boolean;
  brand: SkinAnalyzerConfig;
}

export const useInitSkinAnalyzer = ({
  inView,
  brand,
}: UseInitSkinAnalyzerArgs) => {
  const {
    shopId,
    skinAnalyzer: { env },
  } = useApolloClientHeaders();

  const countryConfig = skaCountryConfig[shopId];

  const { events } = useSkinAnalyzerEvents();
  const { dispatchTrackingEvent } = useTracking();
  const wasRendered = React.useRef(false);

  React.useEffect(() => {
    if (!countryConfig) {
      return;
    }

    if (!inView || wasRendered.current) {
      return;
    }

    trackViewOrSelectPromotion(brand.brandName, 'view_promotion');

    wasRendered.current = true;

    const {
      onAddToCart,
      onAddToFavorites,
      onRemoveFromFavorites,
      onCheckout,
      onClickProduct,
      onExpandProduct,
    } = events;
    const skaConfig: SkaConfig = {
      partner_id: countryConfig.partnerId,
      locale: countryConfig.locale,
      env,
      onAddToCart: async (product) => {
        await Promise.resolve(onAddToCart(product));
      },
      onAddToFavorites: async (product) => {
        await Promise.resolve(onAddToFavorites(product.id));
      },
      onRemoveFromFavorites: async (product) => {
        await Promise.resolve(onRemoveFromFavorites(product.id));
      },
      onClose: () => {
        history.pushState(
          '',
          document.title,
          window.location.pathname + window.location.search
        );
      },
      onCheckout: (_) => {
        onCheckout();
      },
      onClickProduct: (product) => {
        onClickProduct(product);
      },
      onExpandProduct: (product) => {
        onExpandProduct(product);
      },
      onTrackingEvent: (event: SkaTrackingEvent) => {
        if (!event) {
          return;
        }

        dispatchTrackingEvent(event);
      },
      recommendationFilters: {
        brand_includes: brand.brands,
      },
    };

    initSkinAnalyzer(skaConfig);

    return () => {
      unmountSkinAnalyzer();
    };
  }, [countryConfig, env, events, inView, brand, dispatchTrackingEvent]);
};
